import React, { useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { faCircleExclamation, faLinkSlash } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';

import {
  getAccountUsernameFromPathname,
  navigateToCertificatePaymentSuccess,
  useIsCertificateMemberLoggedIn,
  usePublicCertificatePaymentContext,
} from '@share/components';
import {
  ItemTypeEnum,
  type ICertificateItemTemplate,
  type ICertificatePaymentSuccessState,
  type ICertificatePurchaseResult,
  CertificatePaymentModeEnum,
} from '@share/common-types';
import { mapPublicPaymentContextToCertificateTemplate, eligibleCruiseLineNames } from '@share/utils';

import { CertificateCard } from '../components/certificate-card';
import { CertificatePaymentForm } from '../components';

import { PublicPaymentStateCard } from './components';

import styles from './styles.module.scss';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

export function PublicCertificatePaymentWrapper(): React.ReactElement {
  const history = useHistory();
  const { token } = useParams<{ token: string }>();
  const { context, isLoading, isError } = usePublicCertificatePaymentContext({ token });
  const isMemberLoggedIn = useIsCertificateMemberLoggedIn();

  const certificateTemplate = useMemo(
    () => (context ? mapPublicPaymentContextToCertificateTemplate(context) : null),
    [context],
  );

  const isCruiseCertificate = certificateTemplate?.itemTemplates.some(
    (item: ICertificateItemTemplate) => item.type === ItemTypeEnum.Cruise,
  );
  const cruiseLineNames = eligibleCruiseLineNames(certificateTemplate?.eligibleCruiseLines);

  const returnUrl = window.location.href;

  const handlePurchaseSuccess = useCallback(
    (
      purchaseResult: ICertificatePurchaseResult,
      paymentContext?: {
        paymentMode: CertificatePaymentModeEnum;
        instalmentPlanMonths?: number;
      },
    ) => {
      if (!certificateTemplate || !token) {
        return;
      }

      const accountUsername = getAccountUsernameFromPathname(window.location.pathname);

      const state: ICertificatePaymentSuccessState = {
        purchaseResult,
        certificateTemplate,
        paymentMode: paymentContext?.paymentMode,
        instalmentPlanMonths: paymentContext?.instalmentPlanMonths,
        recipientFirstName: context?.recipientFirstName,
      };

      navigateToCertificatePaymentSuccess({
        history,
        accountUsername,
        token,
        state,
        isMemberLoggedIn,
      });
    },
    [certificateTemplate, context?.recipientFirstName, history, isMemberLoggedIn, token],
  );

  if (isLoading) {
    return (
      <PublicPaymentStateCard
        variant="loading"
        titleMessageId="certificates.publicPayment.loading.title"
        descriptionMessageId="certificates.publicPayment.loading.description"
      />
    );
  }

  if (isError || !context || !certificateTemplate || !token) {
    return (
      <PublicPaymentStateCard
        variant="error"
        titleMessageId="certificates.publicPayment.linkExpired.title"
        descriptionMessageId="certificates.publicPayment.linkExpired.description"
        icon={faLinkSlash}
      />
    );
  }

  if (!stripePromise) {
    return (
      <PublicPaymentStateCard
        variant="error"
        titleMessageId="certificates.publicPayment.notConfigured.title"
        descriptionMessageId="certificates.publicPayment.notConfigured.description"
        icon={faCircleExclamation}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.formColumn}>
          <h1 className={styles.pageTitle}>
            <FormattedMessage id="certificates.publicPayment.pageTitle" />
          </h1>
          <p className={styles.pageSubtitle}>
            <FormattedMessage id="certificates.publicPayment.pageSubtitle" />
          </p>

          <Elements stripe={stripePromise}>
            <CertificatePaymentForm
              certificateTemplate={certificateTemplate}
              returnUrl={returnUrl}
              stripePromise={stripePromise}
              variant="public"
              paymentToken={token}
              holderInitialValues={{
                email: context.recipientEmail,
              }}
              onSuccess={handlePurchaseSuccess}
              redeemedruiseLines={
                isCruiseCertificate && cruiseLineNames.length > 0 ? cruiseLineNames : undefined
              }
            />
          </Elements>
        </div>

        <aside className={styles.stickyCardColumn}>
          <CertificateCard
            item={certificateTemplate}
            imageUrl={context.imageUrl ?? undefined}
            variant="summary"
            summaryTaxAmount={null}
            currency={context.currency}
          />
        </aside>
      </div>
    </div>
  );
}
